import { Extension, Node, mergeAttributes } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';

/** The one XSS vector a structured document still admits is a non-web href. */
export const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/** Allow http/https/mailto (and site-relative links); reject javascript:, data:, etc. */
export function isAllowedHref(href: string): boolean {
  if (typeof href !== 'string' || href.trim() === '') return false;
  try {
    // A base lets bare/relative hrefs ("/essays/x") resolve to https → allowed.
    return ALLOWED_LINK_PROTOCOLS.includes(new URL(href, 'https://relative.base').protocol);
  } catch {
    return false;
  }
}

/**
 * pullQuote — a first-class block rendering `<aside class="pull-quote">`. Keeps
 * the existing class so the current CSS keeps working (no `<aside>` smuggled
 * through markdown any more).
 */
export const PullQuote = Node.create({
  name: 'pullQuote',
  group: 'block',
  content: 'inline*',
  defining: true,
  parseHTML() {
    return [{ tag: 'aside.pull-quote' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['aside', mergeAttributes(HTMLAttributes, { class: 'pull-quote' }), 0];
  },
});

/**
 * dropCap — a boolean attribute on the paragraph, rendered as a class on the
 * `<p>`. Idempotent by construction: you cannot add it twice, which the old
 * `<!--drop-cap-->` prepend could.
 */
export const DropCap = Extension.create({
  name: 'dropCap',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          dropCap: {
            default: false,
            parseHTML: (element) => element.classList.contains('drop-cap'),
            renderHTML: (attributes) => (attributes.dropCap ? { class: 'drop-cap' } : {}),
          },
        },
      },
    ];
  },
});

/**
 * image — an inline figure (Spec 39). The stored node carries `assetId` and
 * nothing else (reference-by-ID: correct a caption once and every essay updates).
 * The remaining attributes are transient — renderDocument's resolver populates
 * src/alt/caption/width/height from the asset map before generateHTML, and this
 * renderHTML consumes them to emit <figure><img><figcaption>. They are
 * `rendered: false` so nothing is auto-serialized; the caption is omitted when
 * null. An atom block: indivisible, no editable inner content.
 */
export const Image = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      assetId: { default: null },
      src: { default: null, rendered: false },
      alt: { default: null, rendered: false },
      caption: { default: null, rendered: false },
      width: { default: null, rendered: false },
      height: { default: null, rendered: false },
    };
  },
  parseHTML() {
    return [{ tag: 'figure[data-asset-id]' }];
  },
  renderHTML({ node }) {
    const { assetId, src, alt, caption, width, height } = node.attrs as {
      assetId: string | null;
      src: string | null;
      alt: string | null;
      caption: string | null;
      width: number | null;
      height: number | null;
    };
    const imgAttrs: Record<string, string | number> = { src: src ?? '', alt: alt ?? '' };
    if (width != null) imgAttrs.width = width;
    if (height != null) imgAttrs.height = height;
    const figure: unknown[] = ['figure', { 'data-asset-id': assetId }, ['img', imgAttrs]];
    if (caption) figure.push(['figcaption', {}, caption]);
    return figure as unknown as ['figure', Record<string, unknown>, ...unknown[]];
  },
});

/**
 * The v1 schema — defined once, consumed by the composer AND both renderers. If
 * the editor and renderer disagree, documents render wrong or drop content.
 * StarterKit gives doc/paragraph/text/heading/hardBreak, bold/italic,
 * blockquote/lists/horizontalRule, and history; code/codeBlock/strike are off
 * (not in the toolbar, deferred until wanted). Link is validated; the two custom
 * nodes are pullQuote + the dropCap attribute.
 */
export const extensions = [
  StarterKit.configure({
    heading: { levels: [2, 3, 4] },
    codeBlock: false,
    code: false,
    strike: false,
  }),
  Link.configure({
    openOnClick: false,
    autolink: false,
    protocols: ['http', 'https', 'mailto'],
    isAllowedUri: (url) => isAllowedHref(url),
  }),
  PullQuote,
  DropCap,
  Image,
];
